import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NzContentComponent, NzLayoutComponent } from 'ng-zorro-antd/layout';
import { AnalyticsService } from './service/analytics.service';
import { MenuComponent } from './component/menu/menu.component';
import { EMPTY, from, Observable, Subscription } from 'rxjs';
import { FooterComponent } from './component/footer/footer.component';
import { IconModule } from './icon-module';
import loadjs from 'loadjs';
import { unsubscribe } from './util/rx.util';
import { ConfigService } from './service/config.service';
import { Env } from './dto/enum/env.enum';
import { SentryService } from './service/sentry.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  imports: [
    IconModule,
    RouterOutlet,
    NzLayoutComponent,
    NzContentComponent,
    MenuComponent,
    FooterComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly configService: ConfigService = inject(ConfigService);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly platformId: object = inject(PLATFORM_ID);
  private routerSub$?: Subscription = undefined;

  public ngOnDestroy(): void {
    unsubscribe(this.routerSub$);
  }

  public ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.analyticsService.event('entrypoint', {
      path: this.router.url,
    });
    this.routerSub$ = this.router.events.subscribe({
      next: (event: Event): void => {
        if (event instanceof NavigationEnd) {
          this.analyticsService.event('navigation_end', {
            path: event.urlAfterRedirects,
          });
        }
      },
    });

    this.loadAdUtilScript();
    if (Env.PROD == this.configService.env) {
      this.loadMoneTagCommonNativeBannerScript();
      this.loadMoneTagCommonOnClickAntiAdBlockScript().subscribe({
        next: (): void => this.loadMoneTagCommonOnClickScript(),
      });
    }
  }

  private loadAdUtilScript(): void {
    if (this.document.getElementById('adutil')) {
      return;
    }

    loadjs('adutil.js', {
      async: true,
      numRetries: 1,
      success: (): void => {
        this.analyticsService.event('adblock_exec', {
          enabled: false,
        });
      },
      error: (): void => {
        this.analyticsService.event('adblock_exec', {
          enabled: true,
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null =
          this.document.getElementById('adutil');
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'adutil';
        this.document.body.appendChild(scriptEl);
        return false;
      },
    });
  }

  private loadMoneTagCommonOnClickAntiAdBlockScript(): Observable<void> {
    if (
      this.document.getElementById('monetag-common-onclick-anti-adblock-script')
    ) {
      return EMPTY;
    }

    return from(
      loadjs('9441173.js', {
        async: true,
        returnPromise: true,
        numRetries: this.configService.retryCount,
        error: (): void => {
          console.error(
            'An error occurred during loading Monetag Anti-AdBlock script'
          );
          this.sentryService.captureException({
            error:
              'An error occurred during loading Monetag Anti-AdBlock script',
            severityLevel: 'warning',
            transactionName: 'LOAD_MONETAG_ANTI_ADBLOCK_SCRIPT_ERROR',
          });
        },
        before: (path: string, scriptEl: HTMLElement): boolean => {
          const htmlElement: HTMLElement | null = this.document.getElementById(
            'monetag-common-onclick-anti-adblock-script'
          );
          if (htmlElement) {
            htmlElement.parentElement?.removeChild(htmlElement);
          }
          scriptEl.id = 'monetag-common-onclick-anti-adblock-script';
          this.document.body.appendChild(scriptEl);
          return false;
        },
      })
    );
  }

  private loadMoneTagCommonOnClickScript(): void {
    if (this.document.getElementById('monetag-common-onclick')) {
      return;
    }

    loadjs('https://x7i0.com/tag.min.js', {
      async: true,
      numRetries: this.configService.retryCount,
      error: (): void => {
        console.error('An error occurred during loading Monetag script');
        this.sentryService.captureException({
          error: 'An error occurred during loading Monetag script',
          severityLevel: 'warning',
          transactionName: 'LOAD_MONETAG_SCRIPT_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null = this.document.getElementById(
          'monetag-common-onclick'
        );
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'monetag-common-onclick';
        // @ts-ignore
        scriptEl.dataset.zone = this.configService.moneTag.commonOnClickId;
        // @ts-ignore
        scriptEl.dataset.cfasync = false;
        // @ts-ignore
        scriptEl.onerror = window._lfruaspl;
        // @ts-ignore
        scriptEl.onload = window._pngkqd;
        this.document.body.appendChild(scriptEl);
        return false;
      },
    });
  }

  private loadMoneTagCommonNativeBannerScript(): void {
    if (this.document.getElementById('monetag-common-native-banner')) {
      return;
    }

    loadjs('https://groleegni.net/vignette.min.js', {
      async: true,
      numRetries: this.configService.retryCount,
      error: (): void => {
        console.error('An error occurred during loading Monetag script');
        this.sentryService.captureException({
          error: 'An error occurred during loading Monetag script',
          severityLevel: 'warning',
          transactionName: 'LOAD_MONETAG_SCRIPT_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null = this.document.getElementById(
          'monetag-common-native-banner'
        );
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'monetag-common-native-banner';
        // @ts-ignore
        scriptEl.dataset.zone = this.configService.moneTag.commonNativeBannerId;
        this.document.body.appendChild(scriptEl);
        return false;
      },
    });
  }
}
